import { useState } from 'react';
import { apiClient } from '../../services/apiClient';
import styles from './AuthPages.module.css';
import { brandConfig } from '../../config/brands';
import { useAuthBrandingSync, useAuthLogo, useAuthScheme } from '../../hooks/useAuthScheme';
import { appPath, termsHref, isExternalTerms } from '../../utils/navigation';

export default function ForgotPasswordPage() {
  // ライトテーマ (usodeki 等) では認証カードを明色化する
  useAuthBrandingSync();
  const isLight = useAuthScheme() === 'light';
  const logo = useAuthLogo();
  const wrapperClass = `${styles.wrapper}${isLight ? ` ${styles.light}` : ''}`;
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await apiClient.post('/auth/email/forgot-password', {
        email,
        origin: window.location.origin,
      });
      setSuccess(true);
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(errorMessage || 'リクエストに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={wrapperClass}>
        <div className={styles.container}>
          <div className={styles.logoContainer}>
            <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
          </div>

          <div className={styles.card}>
            <div className={styles.successCard}>
              <h2>メールを送信しました</h2>
              <p>
                {email} にパスワードリセットのメールを送信しました。<br />
                メール内のリンクをクリックして、パスワードをリセットしてください。
              </p>
              <a href={appPath('/')} className={styles.backButton}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
                ログインページへ戻る
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <div className={styles.container}>
        <div className={styles.logoContainer}>
          <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
        </div>

        <div className={styles.card}>
          <h1 className={styles.title}>パスワードをリセット</h1>
          <p className={styles.subtitle}>
            登録したメールアドレスを入力してください。<br />
            パスワードリセットのリンクをお送りします。
          </p>

          {error && <div className={styles.error}>{error}</div>}

          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="email">メールアドレス</label>
              <input
                type="email"
                id="email"
                className={styles.input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@email.com"
                required
                autoComplete="email"
              />
            </div>

            <button type="submit" className={styles.submitButton} disabled={loading}>
              {loading ? '送信中...' : 'リセットリンクを送信'}
            </button>
          </form>

          <div className={styles.links}>
            <a href={appPath('/')} className={styles.link}>ログインページへ戻る</a>
          </div>
        </div>

        <footer className={styles.footer}>
          <div className={styles.footerLinks}>
            <a href={brandConfig.urls.privacy} target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
            <span className={styles.footerDivider}>|</span>
            <a href={termsHref()} {...(isExternalTerms() ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>利用規約</a>
            <span className={styles.footerDivider}>|</span>
            <a href={brandConfig.urls.contact} target="_blank" rel="noopener noreferrer">お問い合わせ</a>
          </div>
        </footer>
      </div>
    </div>
  );
}
